import * as React from 'react';

import type {
  CatalogAdminClient,
  CatalogAdminEditionClient,
  CatalogSupplierClient,
} from '@/api/catalog-client';
import {
  HttpCatalogAdminClient,
  HttpCatalogSupplierClient,
} from '@/api/catalog-client.http';
import { defaultCatalogAdminEditionClient } from '@/api/catalog-edition-client.stub';

// Impl HTTP réelle par défaut (Phase 2, D2a-LECT) : les stubs restent injectables via
// le provider pour les tests. Instances partagées (lectures pures, sans état interne).
const defaultCatalogAdminClient: CatalogAdminClient = new HttpCatalogAdminClient();
const defaultCatalogSupplierClient: CatalogSupplierClient =
  new HttpCatalogSupplierClient();

// =============================================================================
// catalog-client-context.tsx — Injection des clients catalog (DI seam, S8-D2).
// =============================================================================
// Permet de substituer les clients (doubles déterministes en test, impl HTTP réelle
// plus tard) sans toucher l'UI. Par défaut : les Stub*CatalogClient (stub-first).
//
// Contexte AUTO-PORTÉ par le BC catalog : il n'est PAS branché dans le providers.tsx
// global (surface partagée gelée en parallélisme S8) — chaque route catalog enveloppe
// son écran avec le provider localement. Le défaut de contexte = le stub, de sorte que
// l'écran fonctionne hors provider explicite.
//
// P-05 (séparation stricte des mondes) : DEUX contextes DISTINCTS (admin vs supplier),
// jamais partagés. Aucun écran admin ne lit le contexte supplier et inversement.

const CatalogAdminClientContext =
  React.createContext<CatalogAdminClient>(defaultCatalogAdminClient);
const CatalogSupplierClientContext =
  React.createContext<CatalogSupplierClient>(defaultCatalogSupplierClient);

export interface CatalogAdminClientProviderProps {
  client?: CatalogAdminClient | undefined;
  children: React.ReactNode;
}

export function CatalogAdminClientProvider({
  client = defaultCatalogAdminClient,
  children,
}: CatalogAdminClientProviderProps): React.JSX.Element {
  return (
    <CatalogAdminClientContext.Provider value={client}>
      {children}
    </CatalogAdminClientContext.Provider>
  );
}

export function useCatalogAdminClient(): CatalogAdminClient {
  return React.useContext(CatalogAdminClientContext);
}

export interface CatalogSupplierClientProviderProps {
  client?: CatalogSupplierClient | undefined;
  children: React.ReactNode;
}

export function CatalogSupplierClientProvider({
  client = defaultCatalogSupplierClient,
  children,
}: CatalogSupplierClientProviderProps): React.JSX.Element {
  return (
    <CatalogSupplierClientContext.Provider value={client}>
      {children}
    </CatalogSupplierClientContext.Provider>
  );
}

export function useCatalogSupplierClient(): CatalogSupplierClient {
  return React.useContext(CatalogSupplierClientContext);
}

// =============================================================================
// S9-D4 — DI seam du client d'ÉCRITURE admin (A-09/matrice/A-25/A-10).
// =============================================================================
// AUTO-PORTÉ par le BC catalog (comme S8) : ce contexte n'est PAS branché dans le
// providers.tsx global (surface partagée gelée en parallélisme) — chaque route admin
// d'écriture enveloppe son écran avec le provider localement. Le défaut = le stub.
//
// P-05 (séparation stricte des mondes) : contexte DISTINCT des lectures (admin browser
// A-08, supplier F-05). Il reste DANS LE MONDE ADMIN ; aucun écran fournisseur ne le lit.

const CatalogAdminEditionClientContext =
  React.createContext<CatalogAdminEditionClient>(
    defaultCatalogAdminEditionClient,
  );

export interface CatalogAdminEditionClientProviderProps {
  client?: CatalogAdminEditionClient | undefined;
  children: React.ReactNode;
}

export function CatalogAdminEditionClientProvider({
  client = defaultCatalogAdminEditionClient,
  children,
}: CatalogAdminEditionClientProviderProps): React.JSX.Element {
  return (
    <CatalogAdminEditionClientContext.Provider value={client}>
      {children}
    </CatalogAdminEditionClientContext.Provider>
  );
}

export function useCatalogAdminEditionClient(): CatalogAdminEditionClient {
  return React.useContext(CatalogAdminEditionClientContext);
}
